import React, { useState, useEffect } from "react";
import { staffAPI } from "../../services/api";
import { handleApiError } from "../../utils/errorUtils";
import { toast } from "react-toastify";
import {
  FaTimes,
  FaUser,
  FaEnvelope,
  FaPhone,
  FaBirthdayCake,
  FaCamera,
  FaTrash,
  FaSave,
  FaSpinner,
  FaCreditCard,
} from "react-icons/fa";
import SessionManagementTab from "./SessionManagementTab";
import "./CustomerEditModal.css";

/**
 * CustomerEditModal component for editing customer profile information
 * Provides comprehensive form for updating all customer details
 */
const CustomerEditModal = ({ isOpen, onClose, customer, onSuccess }) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    preferredName: "",
    email: "",
    contactNumber: "",
    birthday: "",
    emergencyContactName: "",
    emergencyContactPhone: "",
    communicationPreferences: {
      email: true,
      sms: false,
      phone: false,
    },
    notes: "",
    reason: "",
  });
  const [profilePicture, setProfilePicture] = useState(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("personal");
  const [errors, setErrors] = useState({});

  // Initialize form data when customer changes
  useEffect(() => {
    if (customer) {
      setFormData({
        firstName: customer.firstName || "",
        lastName: customer.lastName || "",
        preferredName: customer.preferredName || "",
        email: customer.email || "",
        contactNumber: customer.contactNumber || "",
        birthday: customer.birthday
          ? new Date(customer.birthday).toISOString().split("T")[0]
          : "",
        emergencyContactName: customer.emergencyContactName || "",
        emergencyContactPhone: customer.emergencyContactPhone || "",
        communicationPreferences: {
          email: customer.communicationPreferences?.email ?? true,
          sms: customer.communicationPreferences?.sms ?? false,
          phone: customer.communicationPreferences?.phone ?? false,
        },
        notes: customer.customerNote || "",
        reason: "",
        // Session usage fields
        saunaMembershipSessionUsage: customer.saunaMembershipSessionUsage || {
          remaining: 0,
          booked: 0,
          completed: 0,
          totalSessions: 0,
          totalCompleted: 0,
        },
        redLightMembershipSessionUsage:
          customer.redLightMembershipSessionUsage || {
            remaining: 0,
            booked: 0,
            completed: 0,
            totalSessions: 0,
            totalCompleted: 0,
          },
        saunaPackageSessionUsage: customer.saunaPackageSessionUsage || {
          remaining: 0,
          booked: 0,
          completed: 0,
          totalSessions: 0,
          totalCompleted: 0,
        },
        redLightPackageSessionUsage: customer.redLightPackageSessionUsage || {
          remaining: 0,
          booked: 0,
          completed: 0,
          totalSessions: 0,
          totalCompleted: 0,
        },
        dropInSaunaCount: customer.dropInSaunaCount || {
          remaining: 0,
          booked: 0,
          completed: 0,
          totalSessions: 0,
          totalCompleted: 0,
        },
        dropInRedLightCount: customer.dropInRedLightCount || {
          remaining: 0,
          booked: 0,
          completed: 0,
          totalSessions: 0,
          totalCompleted: 0,
        },
        giftedSaunaCredits: customer.giftedSaunaCredits || {
          remaining: 0,
          booked: 0,
          completed: 0,
          totalSessions: 0,
          totalCompleted: 0,
          expiresAt: null,
        },
        giftedRedLightCredits: customer.giftedRedLightCredits || {
          remaining: 0,
          booked: 0,
          completed: 0,
          totalSessions: 0,
          totalCompleted: 0,
          expiresAt: null,
        },
        halotherapyEntitlement: customer.halotherapyEntitlement || {
          remaining: 0,
          booked: 0,
          completed: 0,
          totalSessions: 0,
          totalCompleted: 0,
        },
        guestPassEntitlement: customer.guestPassEntitlement || {
          remaining: 0,
          booked: 0,
          completed: 0,
          totalSessions: 0,
          totalCompleted: 0,
        },
        memberGiftSessionEntitlement: customer.memberGiftSessionEntitlement || {
          remaining: 0,
          booked: 0,
          completed: 0,
          totalSessions: 0,
          totalCompleted: 0,
        },
      });
      setProfilePicturePreview(customer.profilePicture || "");
    }
  }, [customer]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name.includes(".")) {
      // Handle nested properties like communicationPreferences.email
      const [parent, child] = name.split(".");
      setFormData((prev) => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: type === "checkbox" ? checked : value,
        },
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        [name]: type === "checkbox" ? checked : value,
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Handle session data updates
  const handleSessionUpdate = (fieldPath, newData) => {
    setFormData((prev) => ({
      ...prev,
      [fieldPath]: newData,
    }));

    // Clear any errors for this field
    if (errors[fieldPath]) {
      setErrors((prev) => ({
        ...prev,
        [fieldPath]: "",
      }));
    }
  };

  // Handle profile picture selection
  const handleProfilePictureChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith("image/")) {
        toast.error("Please select a valid image file");
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image file size must be less than 5MB");
        return;
      }

      setProfilePicture(file);

      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfilePicturePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  // Handle profile picture removal
  const handleRemoveProfilePicture = async () => {
    try {
      await staffAPI.deleteCustomerProfilePicture(customer._id);
      setProfilePicture(null);
      setProfilePicturePreview("");
      toast.success("Profile picture removed successfully");
    } catch (error) {
      handleApiError(
        error,
        "CustomerEditModal.removeProfilePicture",
        (message) => toast.error(message),
        "Error removing profile picture"
      );
    }
  };

  // Validate form data
  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = "First name is required";
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = "Last name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.reason.trim()) {
      newErrors.reason = "Reason for update is required";
    }

    if (
      formData.contactNumber &&
      !/^\+?[\d\s\-()]+$/.test(formData.contactNumber)
    ) {
      newErrors.contactNumber = "Please enter a valid phone number";
    }

    if (
      formData.emergencyContactPhone &&
      !/^\+?[\d\s\-()]+$/.test(formData.emergencyContactPhone)
    ) {
      newErrors.emergencyContactPhone =
        "Please enter a valid emergency contact phone number";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      toast.error("Please fix the errors in the form");
      return;
    }

    setSaving(true);

    try {
      // Check if any session fields have been modified
      const sessionFields = [
        "saunaMembershipSessionUsage",
        "redLightMembershipSessionUsage",
        "saunaPackageSessionUsage",
        "redLightPackageSessionUsage",
        "dropInSaunaCount",
        "dropInRedLightCount",
        "giftedSaunaCredits",
        "giftedRedLightCredits",
        "halotherapyEntitlement",
        "guestPassEntitlement",
        "memberGiftSessionEntitlement",
      ];

      const hasSessionUpdates = sessionFields.some((field) => {
        const originalData = customer[field];
        const newData = formData[field];

        if (!originalData && !newData) return false;
        if (!originalData || !newData) return true;

        return (
          originalData.remaining !== newData.remaining ||
          originalData.booked !== newData.booked ||
          originalData.completed !== newData.completed ||
          originalData.totalSessions !== newData.totalSessions ||
          originalData.totalCompleted !== newData.totalCompleted
        );
      });

      // Update profile data first
      const response = await staffAPI.updateCustomer(customer._id, formData);
      let updatedCustomer = response.data;

      // Update session data if there are changes
      if (hasSessionUpdates) {
        const sessionUpdates = {};
        sessionFields.forEach((field) => {
          if (formData[field]) {
            sessionUpdates[field] = formData[field];
          }
        });

        const sessionResponse = await staffAPI.updateMemberSessions(
          customer._id,
          {
            reason: formData.reason,
            sessionUpdates: sessionUpdates,
          }
        );

        if (sessionResponse.data && sessionResponse.data.customer) {
          updatedCustomer = sessionResponse.data.customer;
        }
      }

      // Upload profile picture if selected
      if (profilePicture) {
        const formDataPicture = new FormData();
        formDataPicture.append("profilePicture", profilePicture);
        const uploadResponse = await staffAPI.uploadCustomerProfilePicture(
          customer._id,
          formDataPicture
        );
        console.log("Profile picture upload response:", uploadResponse);
        if (uploadResponse.data && uploadResponse.data.customer) {
          updatedCustomer = uploadResponse.data.customer;
        }
      }

      toast.success("Customer profile updated successfully");
      onSuccess(updatedCustomer);
      onClose();
    } catch (error) {
      console.error("Error in form submission:", error);
      handleApiError(
        error,
        "CustomerEditModal.submit",
        (message) => toast.error(message),
        "Error updating customer profile"
      );
    } finally {
      setSaving(false);
    }
  };

  // Handle modal close
  const handleClose = () => {
    if (saving) return;
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="customer-edit-modal">
        <div className="modal-header">
          <h2>Edit Customer Profile</h2>
          <button
            className="close-button"
            onClick={handleClose}
            disabled={saving}
          >
            <FaTimes />
          </button>
        </div>

        <div className="modal-tabs">
          <button
            className={`tab-button ${activeTab === "personal" ? "active" : ""}`}
            onClick={() => setActiveTab("personal")}
          >
            <FaUser /> Personal Info
          </button>
          <button
            className={`tab-button ${activeTab === "contact" ? "active" : ""}`}
            onClick={() => setActiveTab("contact")}
          >
            <FaPhone /> Contact & Preferences
          </button>
          <button
            className={`tab-button ${activeTab === "sessions" ? "active" : ""}`}
            onClick={() => setActiveTab("sessions")}
          >
            <FaCreditCard /> Session Credits
          </button>
          <button
            className={`tab-button ${activeTab === "picture" ? "active" : ""}`}
            onClick={() => setActiveTab("picture")}
          >
            <FaCamera /> Profile Picture
          </button>
        </div>

        <form onSubmit={handleSubmit} className="modal-form">
          {/* Reason field - always visible and required */}
          <div className="reason-section">
            <div className="form-group">
              <label htmlFor="reason">Reason for Update *</label>
              <textarea
                id="reason"
                name="reason"
                value={formData.reason}
                onChange={handleInputChange}
                className={errors.reason ? "error" : ""}
                rows="2"
                placeholder="Please explain why you are updating this customer's profile (required for audit purposes)..."
                required
              />
              {errors.reason && (
                <span className="error-message">{errors.reason}</span>
              )}
              <small className="help-text">
                This information is required for audit logging and compliance
                purposes.
              </small>
            </div>
          </div>

          {activeTab === "personal" && (
            <div className="tab-content">
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">
                    <FaUser /> First Name *
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={errors.firstName ? "error" : ""}
                    required
                  />
                  {errors.firstName && (
                    <span className="error-message">{errors.firstName}</span>
                  )}
                </div>

                <div className="form-group">
                  <label htmlFor="lastName">
                    <FaUser /> Last Name *
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={errors.lastName ? "error" : ""}
                    required
                  />
                  {errors.lastName && (
                    <span className="error-message">{errors.lastName}</span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="preferredName">
                  <FaUser /> Preferred Name
                </label>
                <input
                  type="text"
                  id="preferredName"
                  name="preferredName"
                  value={formData.preferredName}
                  onChange={handleInputChange}
                  placeholder="Optional nickname or preferred name"
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">
                  <FaEnvelope /> Email Address *
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={errors.email ? "error" : ""}
                  required
                />
                {errors.email && (
                  <span className="error-message">{errors.email}</span>
                )}
              </div>

              <div className="form-group">
                <label htmlFor="birthday">
                  <FaBirthdayCake /> Birthday
                </label>
                <input
                  type="date"
                  id="birthday"
                  name="birthday"
                  value={formData.birthday}
                  onChange={handleInputChange}
                />
              </div>
            </div>
          )}

          {activeTab === "contact" && (
            <div className="tab-content">
              <div className="form-group">
                <label htmlFor="contactNumber">
                  <FaPhone /> Phone Number
                </label>
                <input
                  type="tel"
                  id="contactNumber"
                  name="contactNumber"
                  value={formData.contactNumber}
                  onChange={handleInputChange}
                  className={errors.contactNumber ? "error" : ""}
                  placeholder="(555) 123-4567"
                />
                {errors.contactNumber && (
                  <span className="error-message">{errors.contactNumber}</span>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="emergencyContactName">
                    Emergency Contact Name
                  </label>
                  <input
                    type="text"
                    id="emergencyContactName"
                    name="emergencyContactName"
                    value={formData.emergencyContactName}
                    onChange={handleInputChange}
                    placeholder="Emergency contact full name"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="emergencyContactPhone">
                    Emergency Contact Phone
                  </label>
                  <input
                    type="tel"
                    id="emergencyContactPhone"
                    name="emergencyContactPhone"
                    value={formData.emergencyContactPhone}
                    onChange={handleInputChange}
                    className={errors.emergencyContactPhone ? "error" : ""}
                    placeholder="(555) 123-4567"
                  />
                  {errors.emergencyContactPhone && (
                    <span className="error-message">
                      {errors.emergencyContactPhone}
                    </span>
                  )}
                </div>
              </div>

              <div className="form-group">
                <label>Communication Preferences</label>
                <div className="checkbox-group">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="communicationPreferences.email"
                      checked={formData.communicationPreferences.email}
                      onChange={handleInputChange}
                    />
                    Email notifications
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="communicationPreferences.sms"
                      checked={formData.communicationPreferences.sms}
                      onChange={handleInputChange}
                    />
                    SMS notifications
                  </label>
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      name="communicationPreferences.phone"
                      checked={formData.communicationPreferences.phone}
                      onChange={handleInputChange}
                    />
                    Phone calls
                  </label>
                </div>
              </div>

              <div className="form-group">
                <label htmlFor="notes">Notes</label>
                <textarea
                  id="notes"
                  name="notes"
                  value={formData.notes}
                  onChange={handleInputChange}
                  rows="4"
                  placeholder="Additional notes about this customer..."
                />
              </div>
            </div>
          )}

          {activeTab === "sessions" && (
            <div className="tab-content">
              <SessionManagementTab
                customer={customer}
                formData={formData}
                onUpdate={handleSessionUpdate}
                errors={errors}
              />
            </div>
          )}

          {activeTab === "picture" && (
            <div className="tab-content">
              <div className="profile-picture-section">
                <div className="current-picture">
                  {profilePicturePreview ? (
                    <div className="picture-container">
                      <img
                        src={profilePicturePreview}
                        alt="Profile"
                        className="profile-picture-preview"
                      />
                      <button
                        type="button"
                        className="remove-picture-btn"
                        onClick={handleRemoveProfilePicture}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  ) : (
                    <div className="no-picture">
                      <FaUser size={60} />
                      <p>No profile picture</p>
                    </div>
                  )}
                </div>

                <div className="picture-upload">
                  <label
                    htmlFor="profilePictureInput"
                    className="upload-button"
                  >
                    <FaCamera />
                    {profilePicturePreview
                      ? "Change Picture"
                      : "Upload Picture"}
                  </label>
                  <input
                    type="file"
                    id="profilePictureInput"
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                    style={{ display: "none" }}
                  />
                  <p className="upload-help">
                    Supported formats: JPG, PNG, GIF (max 5MB)
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="modal-actions">
            <button
              type="button"
              className="cancel-button"
              onClick={handleClose}
              disabled={saving}
            >
              Cancel
            </button>
            <button type="submit" className="save-button" disabled={saving}>
              {saving ? (
                <>
                  <FaSpinner className="spinning" />
                  Saving...
                </>
              ) : (
                <>
                  <FaSave />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CustomerEditModal;
